/**
 * Client for the backend street snapshot (`/api/street/{ticker}`), which wraps FMP:
 * quote, price-target consensus, grades, earnings, estimates, 13F summary, insiders, filings.
 */

import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '@/services/api';

export interface StreetQuote {
  price: number | null;
  change: number | null;
  changePct: number | null;
  marketCap: number | null;
  range52w: string | null;
  beta: number | null;
  averageVolume: number | null;
  sharesOutstanding: number | null;
  floatShares: number | null;
  freeFloatPct: number | null;
  sharesSource: string | null;
  companyName: string | null;
}

export interface StreetConsensus {
  targetHigh: number | null;
  targetLow: number | null;
  targetConsensus: number | null;
  targetMedian: number | null;
  impliedUpsidePct: number | null;
}

export interface StreetGrade {
  date: string | null;
  firm: string | null;
  previousGrade: string | null;
  newGrade: string | null;
  action: string | null;
}

export interface StreetEarningsRow {
  date: string | null;
  epsActual: number | null;
  epsEstimated: number | null;
  revenueActual: number | null;
  revenueEstimated: number | null;
  epsSurprise: number | null;
  revenueSurprisePct: number | null;
}

export interface StreetEstimate {
  date: string | null;
  revenueAvg: number | null;
  revenueLow: number | null;
  revenueHigh: number | null;
  ebitdaAvg: number | null;
  epsAvg: number | null;
  epsLow: number | null;
  epsHigh: number | null;
  numAnalystsRevenue: number | null;
  numAnalystsEps: number | null;
}

export interface StreetOwnership {
  date: string | null;
  investorsHolding: number | null;
  investorsHoldingChange: number | null;
  numberOf13Fshares: number | null;
  numberOf13FsharesChange: number | null;
  totalInvested: number | null;
  totalInvestedChange: number | null;
  ownershipPercent: number | null;
  newPositions: number | null;
  increasedPositions: number | null;
  closedPositions: number | null;
  reducedPositions: number | null;
  totalCalls: number | null;
  totalPuts: number | null;
  putCallRatio: number | null;
}

export type InsiderKind = 'open_market_buy' | 'open_market_sell' | 'tax_withholding' | 'award_or_vest' | 'other';

export interface StreetInsiderTx {
  date: string | null;
  name: string | null;
  role: string | null;
  transactionType: string | null;
  kind: InsiderKind;
  shares: number | null;
  price: number | null;
  security: string | null;
  link: string | null;
}

export interface StreetFiling {
  date: string;
  form: string | null;
  link: string | null;
}

export interface StreetSnapshot {
  version: number;
  ticker: string;
  generatedAt: string;
  quote: StreetQuote;
  consensus: StreetConsensus;
  grades: StreetGrade[];
  earnings: { upcoming: StreetEarningsRow | null; history: StreetEarningsRow[] };
  estimatesQuarterly: StreetEstimate[];
  estimatesAnnual: StreetEstimate[];
  ownership: StreetOwnership;
  insiders: { tallies: Record<InsiderKind, number>; transactions: StreetInsiderTx[] };
  filings: StreetFiling[];
  filingCounts: Record<string, number>;
}

export async function fetchStreetSnapshot(ticker: string, opts: { refresh?: boolean; signal?: AbortSignal } = {}): Promise<StreetSnapshot> {
  const query = opts.refresh ? '?refresh=true' : '';
  const res = await fetch(`${getApiBaseUrl()}/api/street/${encodeURIComponent(ticker)}${query}`, {
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch {
      // non-JSON error body
    }
    throw new Error(`Street snapshot failed: ${detail}`);
  }
  return (await res.json()) as StreetSnapshot;
}

export type StreetState =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: StreetSnapshot; error: null }
  | { status: 'error'; data: null; error: string };

export function useStreetSnapshot(ticker: string): StreetState {
  const [state, setState] = useState<StreetState>({ status: 'loading', data: null, error: null });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', data: null, error: null });
    fetchStreetSnapshot(ticker, { signal: controller.signal })
      .then((data) => setState({ status: 'ready', data, error: null }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: 'error', data: null, error: err instanceof Error ? err.message : String(err) });
      });
    return () => controller.abort();
  }, [ticker]);

  return state;
}

/** Period label like "Q3 26" from an FMP period-end date. */
export function quarterLabel(date: string | null): string {
  if (!date) return '—';
  const [y, m] = date.split('-').map(Number);
  if (!y || !m) return date;
  return `Q${Math.ceil(m / 3)} ${String(y).slice(2)}`;
}

export function yearLabel(date: string | null): string {
  return date ? `FY${date.slice(2, 4)}` : '—';
}

/** Share of 13F-reported shares against the filed basic count (FMP's own % uses a stale denominator). */
export function institutionalSharePct(ownership: StreetOwnership, basicSharesM: number): number | null {
  if (ownership.numberOf13Fshares == null || !basicSharesM) return null;
  return (ownership.numberOf13Fshares / (basicSharesM * 1e6)) * 100;
}
